import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../components/Header';
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import CustomTable from '../../components/CustomTable';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';
import { Button } from '../../components/Button';
import { useNavigate } from 'react-router';
import { formatDateBR, formatMoney } from '../../utils';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

const STATUS_CONFIG = {
  PENDING: { label: 'Pendente', color: '#fff8e1', textColor: '#f57f17' },
  APPROVED: { label: 'Aprovado', color: '#e8f5e9', textColor: '#2e7d32' },
  REJECTED: { label: 'Reprovado', color: '#fce4ec', textColor: '#c62828' },
  CANCELLED: { label: 'Cancelado', color: '#f5f5f5', textColor: '#757575' },
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] ?? {
    label: status ?? '-',
    color: '#f5f5f5',
    textColor: '#757575',
  };
  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.color,
        color: config.textColor,
        fontWeight: 700,
        fontSize: '11px',
        borderRadius: '6px',
        height: '22px',
      }}
    />
  );
};

const FilterDrawer = ({ open, onClose, onFilter, colors, customers = [], resales = [] }) => {
  const { t } = useTranslation();

  const [customer, setCustomer] = useState('');
  const [resale, setResale] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const handleClear = () => {
    setCustomer('');
    setResale('');
    setStatus('');
    setDateFrom('');
    setDateTo('');
  };

  const handleFilter = () => {
    onFilter({
      customer_id: customer,
      resale_id: resale,
      status,
      dateFrom,
      dateTo,
    });
    onClose();
  };

  const selectSx = {
    backgroundColor: '#fff',
    fontSize: '13px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.greenAccent[400] },
  };

  const labelSx = { fontSize: '13px' };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 320, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>{t('common.filter')}</Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, px: 2.5, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel sx={labelSx}>{t('simulation.client')}</InputLabel>
            <Select
              value={customer}
              label={t('simulation.client')}
              onChange={(e) => setCustomer(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">
                <em>{t('forms.select')}</em>
              </MenuItem>

              {customers.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.full_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel sx={labelSx}>{t('simulation.resale')}</InputLabel>
            <Select
              value={resale}
              label={t('simulation.resale')}
              onChange={(e) => setResale(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">
                <em>{t('forms.select')}</em>
              </MenuItem>

              {resales.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item?.company?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel sx={labelSx}>{t('forms.status')}</InputLabel>
            <Select
              value={status}
              label={t('forms.status')}
              onChange={(e) => setStatus(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">
                <em>{t('forms.select')}</em>
              </MenuItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  {cfg.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: '#9e9e9e',
                mb: 1,
                textTransform: 'uppercase',
              }}
            >
              {t('forms.period')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                type="date"
                size="small"
                placeholder={t('forms.startDate')}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, backgroundColor: '#fff', borderRadius: '4px' }}
              />
              <TextField
                type="date"
                size="small"
                placeholder={t('forms.endDate')}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, backgroundColor: '#fff', borderRadius: '4px' }}
              />
            </Box>
          </Box>
        </Box>

        <Divider />
        <Box sx={{ display: 'flex', gap: 1.5, px: 2.5, py: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClear}
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              backgroundColor: '#fff',
              color: colors.grey[300],
              border: `1px solid ${colors.grey[600]}`,
              '&:hover': { backgroundColor: '#f5f5f5' },
            }}
          >
            {t('forms.cleanFilters')}
          </Button>
          <Button
            fullWidth
            onClick={handleFilter}
            sx={{ fontWeight: 700, textTransform: 'uppercase' }}
          >
            {t('common.filter')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

const SimulationSuperAdmin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [simulations, setSimulation] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setIsLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const [customers, setCustomers] = useState([]);
  const [resales, setResales] = useState([]);

  const fetchSimulation = useCallback(async (term = '', filters = {}) => {
    try {
      setIsLoading(true);
      const { data } = await api.get(`/v1/simulation-super-adm/list`, {
        params: {
          name: term || undefined,
          stage: filters.status || undefined,
          resale_id: filters.resale_id || undefined,
          customer_id: filters.customer_id || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
        },
      });
      setSimulation(data?.list ?? []);
    } catch (error) {
      console.error(t('messages.errorFetchingData'), error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFilters = useCallback(async () => {
    try {
      const { data: customersData } = await api.get(`/v1/customer/list`, {
        pageSize: 100,
      });

      const { data: resalesData } = await api.get(`/v1/resale/list`, {
        pageSize: 100,
      });
      setCustomers(customersData?.list ?? []);
      setResales(resalesData?.list ?? []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchSimulation();
    fetchFilters();
  }, [fetchSimulation, fetchFilters]);

  const handleFilter = (filters) => {
    setActiveFilters(filters);
    fetchSimulation(searchTerm, filters);
  };

  const columns = [
    {
      field: 'customer',
      headerName: t('simulation.client'),
      render: (value, row) => (
        <Box
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'uppercase',
            color: colors.grey[200],
          }}
        >
          {row?.customer?.full_name ?? '-'}
        </Box>
      ),
    },
    {
      field: 'company',
      headerName: t('simulation.resale'),
      render: (value, row) => (
        <Box
          sx={{
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'uppercase',
            color: colors.grey[200],
          }}
        >
          {row?.company?.name ?? '-'}
        </Box>
      ),
    },
    {
      field: 'total_value',
      headerName: t('simulation.value'),
      render: (value, row) => formatMoney(row.total_value),
    },
    {
      field: 'created_at',
      headerName: t('simulation.date'),
      render: (value, row) => formatDateBR(row.created_at, false),
    },
    {
      field: 'stage',
      headerName: t('forms.status'),
      render: (value) => <StatusChip status={value} />,
    },
    // IMPLEMENTACAO FUTURA
    // {
    //   field: 'credit_consult',
    //   headerName: 'Consulta de crédito',
    //   render: (value) => (
    //     <Box
    //       sx={{
    //         width: 10,
    //         height: 10,
    //         borderRadius: '50%',
    //         backgroundColor: value ? '#4caf50' : '#e0e0e0',
    //         mx: 'auto',
    //       }}
    //     />
    //   ),
    // },
  ];

  const actions = [
    {
      icon: <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />,
      tooltip: t('common.view'),
      onClick: (row) => navigate(`${row.id}`),
    },
  ];

  if (loading) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box m="20px">
      <Header title={t('simulation.adm.title')} subtitle={t('simulation.adm.subtitle')} />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
        <TextField
          fullWidth
          placeholder={t('forms.searchByName')}
          value={searchTerm}
          onChange={(e) => {
            const value = e.target.value;
            setSearchTerm(value);
            if (value === '') fetchSimulation('', activeFilters);
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') fetchSimulation(searchTerm, activeFilters);
          }}
          variant="outlined"
          InputProps={{
            sx: {
              backgroundColor: '#fff',
              borderRadius: '4px',
              color: colors.grey[400],
              '& fieldset': { borderColor: colors.primary[400] },
              '&:hover fieldset': { borderColor: colors.greenAccent[400] },
              '&.Mui-focused fieldset': { borderColor: colors.greenAccent[400] },
            },
          }}
          sx={{ maxWidth: { xs: '100%', md: '400px' } }}
        />

        <Button
          fullWidth={false}
          onClick={() => fetchSimulation()}
          sx={{ fontWeight: 700, textTransform: 'uppercase' }}
        >
          {t('common.clear')}
        </Button>

        <Button
          fullWidth={false}
          onClick={() => setFilterOpen(true)}
          sx={{
            border: `1px solid ${colors.primary[400]}`,
            px: '1px',
            color: colors.grey[300],
            '&:hover': { borderColor: colors.greenAccent[400], color: colors.greenAccent[400] },
          }}
        >
          <FilterAltOutlinedIcon />
        </Button>
      </Box>

      <CustomTable
        columns={columns}
        rows={simulations}
        actions={actions}
        emptyMessage={t('simulation.noSimulationsFound')}
      />

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onFilter={handleFilter}
        colors={colors}
        customers={customers}
        resales={resales}
      />
    </Box>
  );
};

export default SimulationSuperAdmin;
